import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ArrowLeftRight, RotateCcw, Search, X } from 'lucide-react';
import api, { personMergesApi } from '../../lib/api';
import { apiErrorMessage } from '../../lib/apiError';
import type { MergePreview, MergePreviewSide, PersonDetail } from '../../types';

interface Props { isOpen: boolean; onClose: () => void; person: PersonDetail; onSuccess: () => void }
interface Hit { id: string; firstName: string; lastName: string; email: string | null; position: string | null }

const name = (p: { firstName: string; lastName: string; crmFirstName?: string | null; crmLastName?: string | null }) =>
  `${p.crmFirstName || p.firstName} ${p.crmLastName || p.lastName}`;

// Module-level so it doesn't remount on every keystroke of the parent.
function Side({ side, label }: { side: MergePreviewSide; label: string }) {
  return (
    <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3 text-sm space-y-1">
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className="font-medium text-gray-900 dark:text-gray-100">{name(side)}</div>
      <div className="text-gray-600 dark:text-gray-300">{side.position ?? '—'}</div>
      <div className="text-gray-600 dark:text-gray-300">{side.email ?? '—'}{side.crmEmail ? ` · ${side.crmEmail}` : ''}</div>
      <div className="text-gray-600 dark:text-gray-300">{side.phone ?? '—'}{side.crmPhone ? ` · ${side.crmPhone}` : ''}</div>
      <div className="text-gray-600 dark:text-gray-300">{side.schools.map((s) => s.name).join(', ') || 'No schools'}</div>
      <div className="text-xs text-gray-500">{side.counts.activities} activities · {side.counts.tasks} tasks · {side.counts.lists} lists · {side.blastUserId ? 'Blast-linked' : 'Not in Blast'}</div>
    </div>
  );
}

export function MergePersonSheet({ isOpen, onClose, person, onSuccess }: Props) {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<Hit[]>([]);
  const [otherId, setOtherId] = useState<string | null>(null);
  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [swapped, setSwapped] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) { setHits([]); return; }
    const t = setTimeout(async () => {
      try {
        // excludeDnc=false: /api/people hides Do-Not-Call records by default, and a DNC
        // record is exactly the kind of duplicate someone comes here to fold in.
        const res = await api.get(`/api/people?search=${encodeURIComponent(query.trim())}&limit=8&excludeDnc=false`);
        setHits((res.data.people as Hit[]).filter((h) => h.id !== person.id));
      } catch (e) {
        // Quiet — no toast for a failed typeahead, just clear results.
        apiErrorMessage(e, 'Could not search');
        setHits([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query, person.id]);

  useEffect(() => {
    if (!otherId) { setPreview(null); return; }
    let cancelled = false;
    personMergesApi.preview(person.id, otherId)
      .then(({ preview }) => {
        if (!cancelled) { setPreview(preview); setSwapped(false); }
      })
      .catch((e) => {
        if (!cancelled) toast.error(apiErrorMessage(e, 'Could not load preview'));
      });
    return () => { cancelled = true; };
  }, [otherId, person.id]);

  // Lock background scroll while the dialog is open so the page behind it
  // doesn't scroll along with (or instead of) the dialog body.
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [isOpen]);

  if (!isOpen) return null;

  const selectedHit = otherId ? hits.find((h) => h.id === otherId) ?? null : null;
  const changeSelection = () => { setOtherId(null); setPreview(null); };

  const survivor = preview ? (swapped ? preview.loser : preview.survivor) : null;
  const loser = preview ? (swapped ? preview.survivor : preview.loser) : null;

  const submit = async () => {
    if (!survivor || !loser) return;
    setBusy(true);
    try {
      const { merge } = await personMergesApi.raise({ survivorId: survivor.id, loserId: loser.id, reason: reason.trim() || null });
      toast.success(merge.status === 'APPLIED' ? 'Merged' : 'Sent for approval');
      onSuccess(); onClose();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to merge'));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">

          {/* Header */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Merge duplicate</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6 space-y-5 flex-1 overflow-y-auto">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Which record is a duplicate of {name(person)}?</label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <input className="input pl-8" placeholder="Search for the duplicate…" value={query} onChange={(e) => setQuery(e.target.value)} />
              </div>
              {otherId ? (
                <div className="mt-2 flex items-center justify-between gap-2 rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {selectedHit ? `${selectedHit.firstName} ${selectedHit.lastName}` : 'Selected record'}
                  </span>
                  <button type="button" onClick={changeSelection} className="btn-secondary justify-center whitespace-nowrap py-1 px-2 text-xs">
                    <RotateCcw className="h-3.5 w-3.5 mr-1" />
                    Change
                  </button>
                </div>
              ) : hits.length > 0 && (
                <div className="mt-2 rounded-md border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                  {hits.map((h) => (
                    <button key={h.id} type="button" onClick={() => setOtherId(h.id)} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800">
                      <div className="font-medium">{h.firstName} {h.lastName}</div>
                      <div className="text-xs text-gray-500">{h.email ?? ''}{h.email && h.position ? ' · ' : ''}{h.position ?? ''}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {preview?.error && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">{preview.error}</div>
            )}
            {preview?.blastConflict && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                Both records are Blast accounts. This merge will wait for a manager or data steward, and Blast itself will still have two accounts.
              </div>
            )}

            {survivor && loser && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Side side={survivor} label="Kept" />
                  <Side side={loser} label="Folded in" />
                </div>
                {preview?.swapAllowed && (
                  <button type="button" onClick={() => setSwapped((s) => !s)} className="btn-secondary w-full justify-center">
                    <ArrowLeftRight className="h-4 w-4 mr-1.5" /> Swap which one is kept
                  </button>
                )}
                <p className="text-xs text-gray-500">
                  Everything from "{name(loser)}" moves to "{name(survivor)}". Its own details stay as they are; empty slots get filled from the other record.
                </p>
                <textarea className="input" rows={2} placeholder="Why (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
              </div>
            )}
          </div>

          <div className="bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
            <button type="button" onClick={submit} disabled={busy || !preview || !!preview.error} className="btn-primary w-full justify-center disabled:opacity-50">
              {busy ? 'Merging…' : 'Merge'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
